/* Leaf module: zero imports, zero side effects.
 *
 * Plain numeric / string constants and display helpers used by client
 * components (and anywhere else that needs a lightweight, tree-shakeable
 * value). Keeping this file import-free prevents the bundler from dragging
 * heavy server-only data sources — e.g. src/data/trellis-models.json — into
 * a client chunk just because a component needs a price or a count.
 *
 * ─────────────────────────────────────────────────────────────────────
 * EVERY COUNT BELOW IS A MEASUREMENT. Gate 1.1 of the finishing SOP.
 *
 * MEASURED 2026-09-05 by read-only count(*) against the production
 * Supabase, and by counting src/data/trellis-models.json. Regenerate with:
 *
 *     node scripts/generate-catalog-constants.mjs
 *
 * These were previously "directives" — targets Brian intended to upload
 * into, rendered to buyers as inventory that already existed. The gap was
 * not small:
 *
 *   NOTION_COUNT      said 2,000   → really 1,533   (overstated by 30%)
 *   TEMPLATE_COUNT    said 1,000   → really   927   published packs
 *   NICHE_COUNT_LIVE  said 2,411   → really   279   (overstated 8.6x)
 *   ART_DESIGN_COUNT  said 79,108  → really 79,109
 *
 * NICHE_COUNT_LIVE is the one worth understanding, because it was not a
 * typo. The backend counted live niches with
 * `status in ('approved','producing','live')` — and every single row
 * carries one of those three, so the filter matched the whole table and
 * "live" equalled "total" by construction. 2,131 niches are approved but
 * NOT live. A filter that excludes nothing is not a filter.
 *
 * 69 files import these values. When the catalog grows, run the generator
 * rather than editing a number by hand — a literal typed in here is exactly
 * what this gate exists to remove.
 * ───────────────────────────────────────────────────────────────────── */

/* ── Wall art counts ──────────────────────────────────────────────── */

/* Wall-art DESIGN count — distinct images in shop_families / R2.
 * MEASURED: shop_families = 79,109 rows (2026-09-05).
 * Sitemap shards, evidence-package CSV, sitemap-stats gate use this. */
export const ART_DESIGN_COUNT = 79_109;

/* Average print-configuration variants per design.
 *
 * ⚠️ UNVERIFIED — THE ONE NUMBER HERE WITH NO SOURCE. It was previously
 * computed as Math.round(ART_COUNT / ART_DESIGN_COUNT), while ART_COUNT was
 * itself documented as ART_DESIGN_COUNT x ~26. The multiplier proved itself:
 * a circular derivation, not a measurement. No size, frame or material
 * configuration exists anywhere in src/ to count, so the true figure is not
 * knowable from this repo — it may live with the print partner.
 *
 * Left at the previously assumed 26 rather than silently changed, because
 * dropping it to 1 would understate the catalog just as badly as 26 may
 * overstate it, and guessing downward is still guessing. This needs Brian's
 * confirmation of how many configurations a design actually ships in. */
export const ART_VARIANTS_PER_DESIGN = 26;

/* Wall-art SKU count — each (design x print format x size x frame) is one
 * sellable variant. Derived, and now derived in the honest direction: from
 * the measured design count times the variant multiplier above, rather than
 * a 2,030,056 literal that no table holds. Carries the multiplier's
 * uncertainty — see ART_VARIANTS_PER_DESIGN. */
export const ART_COUNT = ART_DESIGN_COUNT * ART_VARIANTS_PER_DESIGN;

/* ── Niche counts ─────────────────────────────────────────────────── */

/* MEASURED 2026-09-06 after the apex_niches dedupe (54 duplicate rows removed,
 * unique index added): status='live' = 253, total = 2,357. */
export const NICHE_COUNT_LIVE = 253;
export const NICHE_COUNT_TOTAL = 2_357;

/* ── Product-line counts ──────────────────────────────────────────── */

/* Notion operating systems. MEASURED: apex_notion_templates = 1,533 rows,
 * all of them status='published' (2026-09-05). Was 2,000 by directive. */
export const NOTION_COUNT = 1_533;

/* Canva template packs. MEASURED: template_packs with status='published'
 * = 927 (1,090 rows exist in total, so 163 are unpublished). Was 1,000 by
 * directive. Published is the honest figure: an unpublished pack cannot be
 * bought. */
export const TEMPLATE_COUNT = 927;

/* 3D models published.
 *
 * VERIFIED 2026-09-05: src/data/trellis-models.json contains exactly 29,044
 * entries, so this literal is correct and has a real source. There is no
 * Supabase table for 3D models — the manifest IS the source of truth. */
export const TRELLIS_COUNT = 29_044;

/* Also exported as THREE_D_MODEL_COUNT so existing catalog-counts consumers
 * keep working without renaming. */
export const THREE_D_MODEL_COUNT = TRELLIS_COUNT;

/* Distinct searchable items — wall-art designs + 3D models + Notion + templates.
 * Uses DESIGN count, not SKU count: a size variant is not a separate
 * searchable item, it is a selector on one page. */
export const SEARCHABLE_ITEM_COUNT =
  ART_DESIGN_COUNT + TRELLIS_COUNT + NOTION_COUNT + TEMPLATE_COUNT;

/* Wall-art taxonomy facets (used on /digital-art).
 *
 * ⚠️ UNVERIFIED. Counting distinct subjects and styles needs a
 * COUNT(DISTINCT) that PostgREST does not expose, and apex_assets is far
 * past the 1,000-row page cap, so a sampled distinct count would understate
 * both. Left at their previous values and flagged, rather than replaced with
 * a number produced by a method already known to be wrong. Needs an RPC. */
export const ART_SUBJECTS = 500;
export const ART_STYLES = 16;

/* Years Brian has been operating Spiker Carpet & Tile Care (since 2013).
 * A biographical fact, not a catalog count — no query backs it. */
export const YEARS_OPERATING = 13;

/* Catalog-wide total excluding 3D (Pass value-prop math). */
export const CATALOG_COUNT = ART_COUNT + NOTION_COUNT + TEMPLATE_COUNT;

/* ── Pass pricing ─────────────────────────────────────────────────── */

/* ⚠️ GATE 2 MOVES THESE. Prices do not belong in a counts module — that is
 * precisely the fork Gate 2 exists to close ("every price, tier,
 * strike-through, discount, promotion" lives in pricing.ts and nowhere
 * else). Left in place for now because 69 files import from here, and moving
 * them is Gate 2's job — done in one pass, with the grep that proves no
 * price literal survives outside pricing.ts. */

/* Insider Pass annual price (USD). Central so any tier shift updates every
 * upsell surface simultaneously. */
export const INSIDER_PASS_USD = 99;

export const ALL_ACCESS_USD = 299;
export const VAULT_USD = 999;

/* Pass download caps (Final SOP §8.1). */
export const INSIDER_DOWNLOADS_PER_MONTH = 10;
export const ALL_ACCESS_DOWNLOADS_PER_MONTH = 30;
export const VAULT_DOWNLOADS_PER_DAY = 10;

/* Per-product effective unit cost at the Pass scale. Inherits the
 * ART_VARIANTS_PER_DESIGN uncertainty through CATALOG_COUNT — if the
 * multiplier falls, this figure rises. */
export const PER_PRODUCT_PASS_USD = INSIDER_PASS_USD / CATALOG_COUNT;

/* ── Display helpers ──────────────────────────────────────────────── */

export function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

export function fmtPlus(n: number): string {
  return `${fmt(n)}+`;
}

export function fmtShort(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    const decimals = m >= 10 ? 0 : 1;
    return `${m.toFixed(decimals).replace(/\.0$/, "")}M+`;
  }
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K+`;
  return `${n}`;
}
